/* ============================================================
   MISS JE LU — WISHLIST SYSTEM
   File: Assets/wishlist.js
   ============================================================ */

const MJWishlist = (() => {
  const KEY = 'missjelu_wishlist';

  // ---- Storage ----
  function getAll() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch { return []; }
  }

  function save(items) {
    localStorage.setItem(KEY, JSON.stringify(items));
    dispatchUpdate();
  }

  function isInList(id) {
    return getAll().some(p => String(p.id) === String(id));
  }

  function toggle(product) {
    let list = getAll();
    const idx = list.findIndex(p => String(p.id) === String(product.id));
    if (idx > -1) {
      list.splice(idx, 1);
      showToast('Removed from wishlist');
    } else {
      list.push({
        id: String(product.id),
        title: product.title,
        price: product.price,
        compare_price: product.compare_price || null,
        image: product.image,
        url: product.url,
        vendor: product.vendor || '',
        addedAt: Date.now()
      });
      showToast('Added to wishlist ♥');
    }
    save(list);
    updateAllButtons();
    updateHeaderCount();
    return !isInList(product.id);
  }

  function remove(id) {
    save(getAll().filter(p => String(p.id) !== String(id)));
    updateAllButtons();
    updateHeaderCount();
  }

  function count() { return getAll().length; }

  // ---- Events ----
  function dispatchUpdate() {
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { count: count() } }));
  }

  // ---- UI Updates ----
  function updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = btn.dataset.productId;
      const inList = isInList(id);
      btn.classList.toggle('is-active', inList);
      btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
      btn.setAttribute('title', inList ? 'Remove from wishlist' : 'Add to wishlist');
      const svg = btn.querySelector('svg');
      if (svg) svg.style.fill = inList ? '#9f8447' : 'none';
    });
  }

  function updateHeaderCount() {
    const badge = document.getElementById('mj-wishlist-count');
    if (!badge) return;
    const n = count();
    badge.textContent = n;
    badge.style.display = n > 0 ? 'flex' : 'none';
  }

  // ---- Toast ----
  function showToast(msg) {
    let toast = document.getElementById('mj-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'mj-wishlist-toast';
      Object.assign(toast.style, {
        position: 'fixed', bottom: '28px', left: '50%',
        transform: 'translateX(-50%) translateY(12px)',
        background: '#2a1f0f', color: '#fff',
        padding: '11px 24px', borderRadius: '50px',
        fontSize: '13px', fontWeight: '500',
        zIndex: '9999', opacity: '0',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
        whiteSpace: 'nowrap', pointerEvents: 'none'
      });
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
    clearTimeout(toast._t);
    toast._t = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(12px)';
    }, 2500);
  }

  // ---- Click Delegation ----
  function init() {
    updateAllButtons();
    updateHeaderCount();

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const id    = btn.dataset.productId;
      const title = btn.dataset.productTitle;
      const price = btn.dataset.productPrice;
      const cmp   = btn.dataset.productCompare || '';
      const img   = btn.dataset.productImage || '';
      const url   = btn.dataset.productUrl || '';
      const vendor= btn.dataset.productVendor || '';
      toggle({ id, title, price, compare_price: cmp, image: img, url, vendor });
    });

    window.addEventListener('wishlist:updated', updateHeaderCount);
  }

  // ---- Render wishlist page ----
  function renderPage(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    function render() {
      const list = getAll();
      if (list.length === 0) {
        container.innerHTML = `
          <div style="text-align:center;padding:80px 20px;color:#7a6a50">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9f8447" stroke-width="1.5" style="margin-bottom:20px;display:block;margin-left:auto;margin-right:auto">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <h2 style="font-size:20px;font-weight:700;color:#2a1f0f;margin-bottom:10px">Your wishlist is empty</h2>
            <p style="font-size:14px;margin-bottom:28px">Save products you love and come back to them anytime.</p>
            <a href="/collections/all" style="display:inline-block;background:#9f8447;color:#fff;padding:13px 32px;border-radius:50px;text-decoration:none;font-size:14px;font-weight:600">Shop Now</a>
          </div>`;
        return;
      }

      const grid = list.map(p => {
        const price = parseInt(p.price);
        const cmp   = parseInt(p.compare_price);
        const disc  = (cmp > price) ? Math.round((cmp - price) * 100 / cmp) : 0;
        const priceFormatted = 'LE ' + (price / 100).toFixed(0);
        const cmpFormatted  = cmp > price ? 'LE ' + (cmp / 100).toFixed(0) : '';
        return `
          <div class="mj-wl-card" data-id="${p.id}">
            <div class="mj-wl-card__img-wrap">
              <a href="${p.url}">
                <img src="${p.image}" alt="${p.title}" loading="lazy">
              </a>
              ${disc > 0 ? `<span class="mj-wl-card__badge">-${disc}%</span>` : ''}
              <button class="mj-wl-card__remove" data-remove-id="${p.id}" title="Remove from wishlist">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                </svg>
              </button>
            </div>
            <div class="mj-wl-card__info">
              ${p.vendor ? `<p class="mj-wl-card__vendor">${p.vendor}</p>` : ''}
              <a href="${p.url}" class="mj-wl-card__title">${p.title}</a>
              <div class="mj-wl-card__prices">
                <span class="mj-wl-card__price">${priceFormatted}</span>
                ${cmpFormatted ? `<span class="mj-wl-card__compare">${cmpFormatted}</span>` : ''}
              </div>
              <a href="${p.url}" class="mj-wl-card__btn">View Product</a>
            </div>
          </div>`;
      }).join('');

      container.innerHTML = `<div class="mj-wl-grid">${grid}</div>`;

      container.querySelectorAll('[data-remove-id]').forEach(btn => {
        btn.addEventListener('click', () => {
          remove(btn.dataset.removeId);
          render();
        });
      });
    }

    render();
    window.addEventListener('wishlist:updated', render);
  }

  return { init, toggle, isInList, getAll, count, renderPage, remove };
})();

document.addEventListener('DOMContentLoaded', MJWishlist.init);
window.MJWishlist = MJWishlist;